const cfg = JSON.parse(document.getElementById('bb-config')?.textContent || '{}');
const S = cfg.strings || {};
const R = cfg.routes || { root: '/', cart: '/cart', cart_add: '/cart/add', cart_change: '/cart/change', cart_update: '/cart/update' };
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fill = (str, map) => Object.keys(map).reduce((s, k) => s.split('[' + k + ']').join(map[k]), str || '');
const announce = (msg) => { const live = document.getElementById('bb-live'); if (live) { live.textContent = ''; requestAnimationFrame(() => { live.textContent = msg; }); } };

export function formatMoney(cents, format = cfg.moneyFormat || '{{amount}}') {
  const n = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
  const fmt = (v, dec, th = ',', ds = '.') => {
    const [i, d] = (v / 100).toFixed(dec).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? ds + d : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => ({
    amount: fmt(n, 2),
    amount_no_decimals: fmt(n, 0),
    amount_with_comma_separator: fmt(n, 2, '.', ','),
    amount_no_decimals_with_comma_separator: fmt(n, 0, '.', ','),
    amount_with_apostrophe_separator: fmt(n, 2, "'", '.'),
    amount_with_space_separator: fmt(n, 2, ' ', ','),
    amount_no_decimals_with_space_separator: fmt(n, 0, ' ', ',')
  }[key] ?? fmt(n, 2)));
}

/* ---------- cart API ---------- */
async function cartFetch(url, body) {
  let res;
  try {
    res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch (e) {
    throw new Error(S.errorNetwork);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || S.errorGeneric);
    err.status = data.status || res.status;
    throw err;
  }
  return data;
}
const drawerId = () => document.querySelector('bb-cart-drawer')?.dataset.sectionId;
function applySections(sections) {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    $$('[data-bb-rerender]', doc).forEach((fresh) => {
      const key = fresh.dataset.bbRerender;
      $$(`[data-bb-rerender="${key}"]`).forEach((old) => { old.replaceWith(fresh.cloneNode(true)); });
    });
  });
}
function publishCount(count) {
  $$('[data-bb-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });
  $$('[data-bb-cart-open]').forEach((el) => el.setAttribute('aria-label', fill(S.cartCount, { count })));
  document.dispatchEvent(new CustomEvent('bb:cart', { detail: { count } }));
}
export const Cart = {
  async add(items) {
    const id = drawerId();
    const body = { items };
    if (id) { body.sections = [id]; body.sections_url = window.location.pathname; }
    const data = await cartFetch(R.cart_add + '.js', body);
    applySections(data.sections);
    const cart = await cartFetch(R.cart + '.js');
    publishCount(cart.item_count);
    return cart;
  },
  async change(line, quantity) {
    const id = drawerId();
    const body = { line, quantity };
    if (id) { body.sections = [id]; body.sections_url = window.location.pathname; }
    const cart = await cartFetch(R.cart_change + '.js', body);
    applySections(cart.sections);
    publishCount(cart.item_count);
    return cart;
  },
  async update(payload) {
    const id = drawerId();
    const body = { ...payload };
    if (id) { body.sections = [id]; body.sections_url = window.location.pathname; }
    const cart = await cartFetch(R.cart_update + '.js', body);
    applySections(cart.sections);
    publishCount(cart.item_count);
    return cart;
  }
};

/* ---------- dialog helpers (native <dialog> gives focus containment + Escape) ---------- */
function openDialog(dlg, opener) {
  if (!dlg || dlg.open) return;
  dlg._opener = opener || document.activeElement;
  dlg.showModal();
  document.documentElement.style.overflow = 'hidden';
  $$('[aria-controls="' + dlg.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
}
function closeDialog(dlg) {
  if (!dlg || !dlg.open) return;
  dlg.close();
}
document.addEventListener('close', (e) => {
  const dlg = e.target;
  if (!(dlg instanceof HTMLDialogElement)) return;
  document.documentElement.style.overflow = '';
  $$('[aria-controls="' + dlg.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
  if (dlg._opener && document.contains(dlg._opener)) dlg._opener.focus();
}, true);
document.addEventListener('click', (e) => {
  const closer = e.target.closest('[data-bb-close]');
  if (closer) { closeDialog(closer.closest('dialog')); return; }
  if (e.target instanceof HTMLDialogElement && e.target.open) {
    const r = e.target.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(e.target);
  }
});

/* ---------- media: uploaded video + external facade ---------- */
customElements.define('bb-video', class extends HTMLElement {
  connectedCallback() {
    const v = this.querySelector('video');
    if (!v || !this.hasAttribute('data-autoplay')) return;
    v.muted = true;
    if (reduceMotion()) return;
    this.io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) v.play().catch(() => {}); else v.pause();
    }, { threshold: 0.25 });
    this.io.observe(this);
  }
  disconnectedCallback() { this.io?.disconnect(); }
});
customElements.define('bb-video-facade', class extends HTMLElement {
  connectedCallback() {
    this.querySelector('[data-bb-play]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const tpl = this.querySelector('template');
      if (!tpl) return;
      this.replaceChildren(tpl.content.cloneNode(true));
      this.querySelector('iframe')?.focus();
    });
  }
});

/* ---------- announcement bar ---------- */
customElements.define('bb-announcement', class extends HTMLElement {
  connectedCallback() {
    this.msgs = $$('.bb-ann__msg', this);
    this.btn = $('[data-bb-ann-toggle]', this);
    if (this.msgs.length < 2 || !this.hasAttribute('data-rotate')) return;
    this.i = 0;
    this.speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
    this.btn?.addEventListener('click', () => (this.timer ? this.stop(true) : this.start()));
    this.addEventListener('focusin', () => this.stop());
    if (!reduceMotion()) this.start(); else this.setBtn(false);
  }
  show(i) { this.msgs.forEach((m, k) => { m.setAttribute('aria-hidden', k === i ? 'false' : 'true'); $$('a', m).forEach((a) => (a.tabIndex = k === i ? 0 : -1)); }); }
  start() { this.stop(); this.timer = setInterval(() => { this.i = (this.i + 1) % this.msgs.length; this.show(this.i); }, this.speed); this.setBtn(true); }
  stop() { clearInterval(this.timer); this.timer = null; this.setBtn(false); }
  setBtn(playing) { if (!this.btn) return; this.btn.setAttribute('aria-pressed', playing ? 'false' : 'true'); this.btn.setAttribute('aria-label', playing ? S.pause : S.play); this.btn.innerHTML = playing ? this.dataset.iconPause : this.dataset.iconPlay; }
  disconnectedCallback() { this.stop(); }
});

/* ---------- marquee ---------- */
customElements.define('bb-marquee', class extends HTMLElement {
  connectedCallback() {
    if (reduceMotion()) this.setAttribute('data-paused', '');
    this.querySelector('[data-bb-mq-toggle]')?.addEventListener('click', (e) => {
      const paused = this.toggleAttribute('data-paused');
      e.currentTarget.setAttribute('aria-pressed', paused ? 'true' : 'false');
    });
  }
});

/* ---------- header ---------- */
customElements.define('bb-header', class extends HTMLElement {
  connectedCallback() {
    const hdr = this.querySelector('.bb-hdr');
    const setH = () => document.documentElement.style.setProperty('--hdr-h', hdr.offsetHeight + 'px');
    setH(); window.addEventListener('resize', setH, { passive: true });
    if (hdr.hasAttribute('data-transparent')) {
      const onScroll = () => hdr.classList.toggle('is-scrolled', window.scrollY > 40);
      onScroll(); window.addEventListener('scroll', onScroll, { passive: true });
    }
    $$('[data-bb-toggle]', this).forEach((btn) => {
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        $$('[data-bb-toggle][aria-expanded="true"]', this).forEach((o) => { if (o !== btn) { o.setAttribute('aria-expanded', 'false'); document.getElementById(o.getAttribute('aria-controls')).hidden = true; } });
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
        if (open) panel.querySelector('input,a,button')?.focus();
      });
      panel?.addEventListener('keydown', (e) => { if (e.key === 'Escape') { btn.setAttribute('aria-expanded', 'false'); panel.hidden = true; btn.focus(); } });
    });
    document.addEventListener('click', (e) => {
      if (this.contains(e.target)) return;
      $$('[data-bb-toggle][data-bb-dismiss][aria-expanded="true"]', this).forEach((o) => { o.setAttribute('aria-expanded', 'false'); document.getElementById(o.getAttribute('aria-controls')).hidden = true; });
    });
    $$('[data-bb-dialog-open]', this).forEach((b) => b.addEventListener('click', () => openDialog(document.getElementById(b.getAttribute('aria-controls')), b)));
  }
});

/* ---------- cart drawer ---------- */
customElements.define('bb-cart-drawer', class extends HTMLElement {
  connectedCallback() {
    this.dlg = $('dialog', this);
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-bb-cart-open]');
      if (!b || !this.dlg) return;
      e.preventDefault();
      openDialog(this.dlg, b);
    });
    this.addEventListener('click', (e) => {
      const b = e.target.closest('[data-bb-line]');
      if (!b) return;
      this.run(() => Cart.change(parseInt(b.dataset.bbLine, 10), parseInt(b.dataset.qty, 10)), b);
    });
    this.addEventListener('submit', (e) => {
      const f = e.target.closest('[data-bb-upsell], [data-bb-discount]');
      if (!f) return;
      e.preventDefault();
      if (f.matches('[data-bb-upsell]')) this.run(() => Cart.add([{ id: parseInt(f.elements.id.value, 10), quantity: 1 }]), f.querySelector('button'));
      else this.run(() => Cart.update({ discount: f.elements.discount.value.trim() }), f.querySelector('button'));
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-bb-note]')) Cart.update({ note: e.target.value }).catch((err) => this.msg(err.message));
    });
  }
  open(opener) { openDialog(this.dlg, opener); }
  msg(text) { const m = $('[data-bb-cart-msg]', this); if (m) { m.textContent = text || ''; m.hidden = !text; } }
  async run(fn, origin) {
    const key = origin?.dataset.bbFocus;
    this.classList.add('is-busy');
    try { await fn(); this.msg(''); } catch (err) { this.msg(err.message); }
    this.classList.remove('is-busy');
    const back = key ? $(`[data-bb-focus="${key}"]`, this) : null;
    (back || $('[data-bb-close]', this))?.focus();
  }
});

/* ---------- product: variants, tiers, qty, gallery sync, ATC ---------- */
customElements.define('bb-product', class extends HTMLElement {
  connectedCallback() {
    try { this.data = JSON.parse($('[data-bb-product-json]', this).textContent); } catch (e) { return; }
    this.variants = this.data.variants;
    this.form = $('form[data-bb-form]', this);
    this.btn = $('[data-bb-atc]', this);
    this.qtyInput = $('[data-bb-qty]', this);
    this.err = $('[data-bb-error]', this);
    this.pickWrap = $('[data-bb-picks]', this);
    this.pickRows = $$('[data-bb-pick-row]', this);
    this.pickIdx = this.pickWrap ? parseInt(this.pickWrap.dataset.optionIndex, 10) || 0 : 0;
    this.current = this.variants.find((v) => String(v.id) === this.form.elements.id.value) || this.variants[0];
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-bb-option]')) this.onOption();
      else if (e.target.matches('[data-bb-tier-input]')) this.onTier(e.target);
      else if (e.target.matches('[data-bb-pick]')) this.onPick(e.target);
      else if (e.target === this.qtyInput) this.setQty(parseInt(this.qtyInput.value, 10));
    });
    $$('[data-bb-step]', this).forEach((b) => b.addEventListener('click', () => this.setQty(this.qty() + parseInt(b.dataset.bbStep, 10))));
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
    document.addEventListener('bb:atc', () => this.add());
    const checkedTier = $('[data-bb-tier-input]:checked', this);
    if (checkedTier) this.onTier(checkedTier, true); else this.update(false);
  }
  selected() { return $$('[data-bb-optgroup]', this).map((fs) => $('input:checked', fs)?.value ?? null); }
  match(sel) { return this.variants.find((v) => v.options.every((o, i) => o === sel[i])); }
  onOption() {
    const v = this.match(this.selected());
    if (!v) { this.showError(S.comboUnavailable); return; }
    this.current = v;
    this.pickRows.forEach((row) => this.setPick(row, v.options[this.pickIdx]));
    this.update(true);
  }
  setPick(row, value) {
    const r = $$('[data-bb-pick]', row).find((x) => x.value === value);
    if (r && !r.disabled) r.checked = true;
  }
  pickCount() {
    const t = this.tier();
    if (!this.pickWrap || !t || t.dataset.target !== 'qty') return 0;
    const n = parseInt(t.dataset.qty, 10) || 1;
    return n > 1 && n === this.qty() ? Math.min(n, this.pickRows.length) : 0;
  }
  pickVariant(value) {
    const sel = this.current.options.slice();
    sel[this.pickIdx] = value;
    return this.match(sel);
  }
  syncPicks() {
    if (!this.pickWrap) return;
    const n = this.pickCount();
    this.pickWrap.hidden = n < 2;
    this.pickRows.forEach((row, i) => {
      row.hidden = i >= n;
      $$('[data-bb-pick]', row).forEach((r) => { const m = this.pickVariant(r.value); r.disabled = !m || !m.available; });
      if (!$('[data-bb-pick]:checked', row)) this.setPick(row, this.current.options[this.pickIdx]);
      const lbl = $('[data-bb-pick-value]', row);
      const chosen = $('[data-bb-pick]:checked', row);
      if (lbl) lbl.textContent = chosen ? chosen.value : '';
    });
  }
  onPick(input) {
    const m = this.pickVariant(input.value);
    if (m && m.featured_media_id) this.querySelector('bb-gallery')?.show(m.featured_media_id);
    this.render();
  }
  tier() { return $('[data-bb-tier-input]:checked', this); }
  onTier(input, silent) {
    const t = input.dataset;
    if (t.target === 'variant') {
      const v = this.variants.find((x) => String(x.id) === t.variant);
      if (v) {
        this.current = v;
        v.options.forEach((o, i) => { const r = $$(`[data-bb-optgroup="${i}"] input`, this).find((x) => x.value === o); if (r) r.checked = true; });
      }
      this.setQty(1, true);
    } else if (t.target === 'qty') {
      this.setQty(parseInt(t.qty, 10) || 1, true);
    } else {
      this.setQty(1, true);
    }
    this.update(!silent);
  }
  qty() { return Math.max(1, parseInt(this.qtyInput?.value, 10) || 1); }
  setQty(q, fromTier) {
    const max = this.current.inventory_policy === 'deny' && this.current.inventory_management ? Math.max(1, this.current.inventory_quantity) : 999;
    q = Math.min(Math.max(1, q || 1), max);
    if (this.qtyInput) this.qtyInput.value = q;
    const dec = $('[data-bb-step="-1"]', this); if (dec) dec.disabled = q <= 1;
    if (!fromTier) {
      const t = this.tier();
      if (t && t.dataset.target === 'qty' && parseInt(t.dataset.qty, 10) !== q) {
        const same = $$('[data-bb-tier-input][data-target="qty"]', this).find((x) => parseInt(x.dataset.qty, 10) === q);
        if (same) same.checked = true;
      }
    }
    this.render();
  }
  lineItems() {
    const t = this.tier();
    const q = this.qty();
    if (t && t.dataset.target === 'bundle') return [{ id: parseInt(t.dataset.bundleVariant, 10), quantity: q, price: parseInt(t.dataset.bundlePrice, 10), available: t.dataset.bundleAvailable === 'true' }];
    const n = this.pickCount();
    if (n > 1) {
      const lines = new Map();
      for (let i = 0; i < n; i += 1) {
        const chosen = $('[data-bb-pick]:checked', this.pickRows[i]);
        const v = (chosen && this.pickVariant(chosen.value)) || this.current;
        const line = lines.get(v.id) || { id: v.id, quantity: 0, price: v.price, available: v.available };
        line.quantity += 1;
        lines.set(v.id, line);
      }
      return Array.from(lines.values());
    }
    return [{ id: this.current.id, quantity: q, price: this.current.price, available: this.current.available }];
  }
  update(fromUser) {
    const v = this.current;
    this.form.elements.id.value = v.id;
    this.showError('');
    const sel = v.options;
    $$('[data-bb-optgroup]', this).forEach((fs) => {
      const i = parseInt(fs.dataset.bbOptgroup, 10);
      const lbl = $('[data-bb-opt-value]', fs); if (lbl) lbl.textContent = sel[i];
      $$('.bb-val', fs).forEach((wrap) => {
        const input = $('input', wrap);
        const test = sel.slice(); test[i] = input.value;
        const m = this.match(test);
        const state = m ? (m.available ? 'ok' : 'soldout') : 'unavailable';
        wrap.dataset.state = state;
        input.disabled = state === 'unavailable';
        const sr = $('[data-bb-val-state]', wrap);
        if (sr) sr.textContent = state === 'soldout' ? ' – ' + S.soldOut : state === 'unavailable' ? ' – ' + S.unavailable : '';
      });
    });
    $$('[data-bb-tier-input][data-target="variant"]', this).forEach((r) => {
      const tv = this.variants.find((x) => String(x.id) === r.dataset.variant);
      r.disabled = !tv || !tv.available;
    });
    this.render();
    if (fromUser) {
      if (v.featured_media_id) this.querySelector('bb-gallery')?.show(v.featured_media_id);
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href); url.searchParams.set('variant', v.id); window.history.replaceState({}, '', url.toString());
      }
      document.dispatchEvent(new CustomEvent('bb:variant', { detail: { variant: v, product: this } }));
    }
  }
  render() {
    this.syncPicks();
    const v = this.current;
    const q = this.qty();
    const items = this.lineItems();
    const tierSel = $('[data-bb-tier-input]:checked', this);
    const tierFits = tierSel && (tierSel.dataset.target !== 'qty' || parseInt(tierSel.dataset.qty, 10) === q);
    const tierDisc = tierFits ? parseInt(tierSel.dataset.discount, 10) || 0 : 0;
    const total = Math.max(items.reduce((s, i) => s + i.price * i.quantity, 0) - tierDisc, 0);
    const available = items.every((i) => i.available);
    const set = (sel, txt, show = true) => $$(sel, this).forEach((el) => { el.textContent = txt; el.hidden = !show; });
    set('[data-bb-price]', formatMoney(v.price));
    const onSale = v.compare_at_price && v.compare_at_price > v.price;
    set('[data-bb-compare]', onSale ? formatMoney(v.compare_at_price) : '', !!onSale);
    set('[data-bb-save]', onSale ? this.dataset.saveLabel.replace('[amount]', formatMoney(v.compare_at_price - v.price)) : '', !!onSale);
    set('[data-bb-unit]', v.unit_price_html || '', !!v.unit_price_html);
    set('[data-bb-sku]', v.sku ? this.dataset.skuLabel.replace('[sku]', v.sku) : '', !!v.sku);
    const stock = $('[data-bb-stock]', this);
    if (stock) {
      const thr = parseInt(stock.dataset.threshold, 10) || 0;
      const real = v.inventory_management === 'shopify' && v.available && v.inventory_quantity > 0 && v.inventory_quantity <= thr;
      stock.textContent = real ? fill(S.onlyLeft, { count: v.inventory_quantity }) : '';
      stock.hidden = !real;
    }
    $$('[data-bb-tier-input]', this).forEach((r) => {
      const label = r.nextElementSibling;
      const totEl = $('[data-bb-tier-total]', label);
      const eachEl = $('[data-bb-tier-each]', label);
      const wasEl = $('[data-bb-tier-was]', label);
      let unit = v.price, n = 1, was = v.compare_at_price;
      if (r.dataset.target === 'qty') n = parseInt(r.dataset.qty, 10) || 1;
      if (r.dataset.target === 'variant') { const tv = this.variants.find((x) => String(x.id) === r.dataset.variant); if (tv) { unit = tv.price; was = tv.compare_at_price; } }
      if (r.dataset.target === 'bundle') { unit = parseInt(r.dataset.bundlePrice, 10); was = parseInt(r.dataset.bundleCompare, 10) || 0; }
      const disc = parseInt(r.dataset.discount, 10) || 0;
      const full = unit * n;
      const now = Math.max(full - disc, 0);
      if (totEl) totEl.textContent = formatMoney(now);
      if (eachEl) eachEl.textContent = n > 1 ? fill(S.each, { price: formatMoney(Math.round(now / n)) }) : '';
      if (wasEl) {
        const ref = disc > 0 ? full : (was && was > unit ? was * n : 0);
        wasEl.textContent = ref > now ? formatMoney(ref) : '';
        wasEl.hidden = !(ref > now);
      }
    });
    if (this.btn.getAttribute('aria-busy') !== 'true') {
      this.btn.disabled = !available;
      this.btn.textContent = available ? `${S.addToCart} · ${formatMoney(total)}` : S.soldOut;
    }
    document.dispatchEvent(new CustomEvent('bb:product-render', { detail: { variant: v, quantity: q, total, available, title: this.data.title } }));
  }
  showError(msg) { if (this.err) { this.err.textContent = msg || ''; this.err.hidden = !msg; } }
  async add() {
    const items = this.lineItems();
    if (!items.every((i) => i.available)) { this.showError(S.soldOut); return; }
    this.btn.setAttribute('aria-busy', 'true');
    this.btn.disabled = true;
    this.btn.textContent = S.adding;
    try {
      await Cart.add(items.map(({ id, quantity }) => ({ id, quantity })));
      const drawer = document.querySelector('bb-cart-drawer');
      if (drawer && drawer.dlg) drawer.open(this.btn); else window.location.href = R.cart;
    } catch (err) {
      this.showError(err.message);
      announce(err.message);
    }
    this.btn.removeAttribute('aria-busy');
    this.render();
  }
});

/* ---------- gallery ---------- */
customElements.define('bb-gallery', class extends HTMLElement {
  connectedCallback() {
    this.track = $('[data-bb-track]', this);
    this.slides = $$('[data-bb-slide]', this);
    this.thumbs = $$('[data-bb-thumb]', this);
    this.count = $('[data-bb-count]', this);
    this.i = 0;
    $('[data-bb-prev]', this)?.addEventListener('click', () => this.go(this.i - 1));
    $('[data-bb-next]', this)?.addEventListener('click', () => this.go(this.i + 1));
    this.thumbs.forEach((t, k) => t.addEventListener('click', () => this.go(k)));
    this.track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const k = Math.round(this.track.scrollLeft / this.track.clientWidth);
        if (k !== this.i) this.mark(k);
      });
    }, { passive: true });
    this.track?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.i + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.i - 1); }
    });
    $$('[data-bb-zoom]', this).forEach((b) => b.addEventListener('click', () => this.zoom(b)));
    const start = parseInt(this.dataset.start, 10) || 0;
    if (start && this.track) requestAnimationFrame(() => { this.track.scrollLeft = this.track.clientWidth * start; this.mark(start); });
  }
  mark(k) {
    this.i = k;
    this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === k ? 'true' : 'false'));
    this.slides.forEach((s, n) => { if (n !== k) s.querySelector('video')?.pause(); });
    if (this.count) this.count.textContent = `${k + 1} / ${this.slides.length}`;
  }
  go(k) {
    const n = this.slides.length; if (!n) return;
    k = (k + n) % n;
    this.track.scrollTo({ left: this.track.clientWidth * k, behavior: reduceMotion() ? 'auto' : 'smooth' });
    this.mark(k);
  }
  show(mediaId) {
    const k = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (k > -1) this.go(k);
  }
  zoom(btn) {
    const lb = document.getElementById('bb-lightbox');
    const stage = lb && $('[data-bb-lb-stage]', lb);
    const media = btn.closest('[data-bb-slide]')?.querySelector('.bb-media');
    if (!stage || !media) return;
    const clone = media.cloneNode(true);
    $$('img', clone).forEach((img) => { img.loading = 'eager'; img.sizes = '100vw'; });
    stage.replaceChildren(clone);
    openDialog(lb, btn);
  }
});

/* ---------- sticky add-to-cart ---------- */
customElements.define('bb-sticky-atc', class extends HTMLElement {
  connectedCallback() {
    this.bar = $('.bb-satc', this);
    this.btn = $('[data-bb-satc-btn]', this);
    this.btn?.addEventListener('click', () => document.dispatchEvent(new CustomEvent('bb:atc')));
    document.addEventListener('bb:product-render', (e) => this.sync(e.detail));
    const target = document.querySelector('[data-bb-atc]');
    if (!target) return;
    new IntersectionObserver(([en]) => this.toggle(!en.isIntersecting && en.boundingClientRect.top < 0)).observe(target);
  }
  sync({ variant, total, available }) {
    const v = $('[data-bb-satc-variant]', this); if (v) v.textContent = variant.title === 'Default Title' ? '' : variant.title;
    const p = $('[data-bb-satc-price]', this); if (p) p.textContent = formatMoney(total);
    if (this.btn) { this.btn.disabled = !available; this.btn.textContent = available ? S.addToCart : S.soldOut; }
  }
  toggle(on) {
    const shown = on && getComputedStyle(this.bar).display !== 'none';
    this.bar.classList.toggle('is-visible', shown);
    this.bar.inert = !shown;
    document.body.classList.toggle('bb-has-satc', shown);
    document.body.style.setProperty('--satc-h', shown ? this.bar.offsetHeight + 'px' : '0px');
  }
});

/* ---------- carousels: arrows, dots, mouse drag, native swipe + snap ---------- */
customElements.define('bb-carousel', class extends HTMLElement {
  connectedCallback() {
    this.track = $('[data-bb-car-track]', this);
    if (!this.track) return;
    this.ctl = $('[data-bb-car-ctl]', this);
    this.prev = $('[data-bb-car-prev]', this);
    this.next = $('[data-bb-car-next]', this);
    this.dotsEl = $('[data-bb-car-dots]', this);
    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.sync()); }, { passive: true });
    this.track.addEventListener('keydown', (e) => {
      if (e.target !== this.track) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
    });
    this.bindDrag();
    this.ro = new ResizeObserver(() => this.layout());
    this.ro.observe(this.track);
  }
  disconnectedCallback() { this.ro?.disconnect(); }
  items() { return Array.from(this.track.children).filter((el) => !el.hidden); }
  scrollable() { return this.track.scrollWidth > this.track.clientWidth + 4; }
  pageWidth() { const first = this.items()[0]; if (!first) return this.track.clientWidth; const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0; return first.getBoundingClientRect().width + gap; }
  layout() {
    const on = this.scrollable();
    if (this.ctl) this.ctl.hidden = !on;
    if (this.dotsEl) {
      const count = on ? Math.max(1, Math.round((this.track.scrollWidth - this.track.clientWidth) / this.pageWidth()) + 1) : 0;
      if (count !== this.dotCount) {
        this.dotCount = count;
        this.dotsEl.replaceChildren(...Array.from({ length: count }, (_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', fill(S.goToSlide, { index: i + 1 }));
          b.addEventListener('click', () => this.track.scrollTo({ left: i * this.pageWidth(), behavior: reduceMotion() ? 'auto' : 'smooth' }));
          return b;
        }));
      }
      this.dotsEl.hidden = count < 2;
    }
    this.sync();
  }
  sync() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = Math.abs(this.track.scrollLeft);
    if (this.prev) this.prev.disabled = x <= 2;
    if (this.next) this.next.disabled = x >= max - 2;
    if (this.dotsEl && this.dotCount) {
      const i = x >= max - 2 ? this.dotCount - 1 : Math.round(x / this.pageWidth());
      Array.from(this.dotsEl.children).forEach((d, k) => d.setAttribute('aria-current', k === i ? 'true' : 'false'));
    }
  }
  step(dir) { this.track.scrollBy({ left: dir * this.pageWidth(), behavior: reduceMotion() ? 'auto' : 'smooth' }); }
  bindDrag() {
    let startX = 0, startScroll = 0, moved = false, active = false;
    this.track.addEventListener('pointerdown', (e) => {
      moved = false;
      if (e.pointerType !== 'mouse' || e.button !== 0 || !this.scrollable() || e.target.closest('a, button, input, video, iframe')) return;
      active = true; moved = false; startX = e.clientX; startScroll = this.track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!active) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 6) { moved = true; this.track.classList.add('is-dragging'); }
      if (moved) this.track.scrollLeft = startScroll - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!active) return;
      active = false;
      if (!moved) return;
      const snapTo = Math.round(this.track.scrollLeft / this.pageWidth()) * this.pageWidth();
      this.track.classList.remove('is-dragging');
      this.track.scrollTo({ left: snapTo, behavior: reduceMotion() ? 'auto' : 'smooth' });
    });
    this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
  }
  reveal(el) { const li = el.closest('[data-bb-car-track] > *'); if (li) this.track.scrollTo({ left: li.offsetLeft - this.track.offsetLeft, behavior: 'auto' }); }
});

/* ---------- review wall: filters + load more ---------- */
customElements.define('bb-review-wall', class extends HTMLElement {
  connectedCallback() {
    this.cards = $$('[data-bb-rv]', this);
    this.step = parseInt(this.dataset.step, 10) || 6;
    this.limit = parseInt(this.dataset.initial, 10) || 6;
    this.filter = 'all';
    this.more = $('[data-bb-more]', this);
    this.none = $('[data-bb-rv-none]', this);
    $$('[data-bb-filter]', this).forEach((chip) => chip.addEventListener('click', () => {
      $$('[data-bb-filter]', this).forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      this.filter = chip.dataset.bbFilter;
      this.limit = this.step;
      this.apply();
    }));
    this.more?.addEventListener('click', () => {
      const firstNew = this.matching()[this.limit];
      this.limit += this.step;
      this.apply();
      firstNew?.querySelector('blockquote, p')?.setAttribute('tabindex', '-1');
      firstNew?.querySelector('blockquote, p')?.focus();
    });
  }
  matching() {
    return this.cards.filter((c) => this.filter === 'all' || (this.filter === 'photo' && c.dataset.photo === 'true') || (this.filter === '5' && c.dataset.rating === '5'));
  }
  apply() {
    const match = this.matching();
    this.cards.forEach((c) => { c.hidden = true; });
    match.slice(0, this.limit).forEach((c) => { c.hidden = false; });
    if (this.more) this.more.hidden = match.length <= this.limit;
    if (this.none) this.none.hidden = match.length > 0;
    announce(fill(S.reviewsShown, { count: Math.min(match.length, this.limit) }));
  }
});

/* ---------- accordions: optional one-open-at-a-time ---------- */
document.addEventListener('toggle', (e) => {
  const d = e.target;
  if (!(d instanceof HTMLDetailsElement) || !d.open) return;
  const group = d.closest('[data-bb-single-open]');
  if (group) $$('details[open]', group).forEach((o) => { if (o !== d) o.open = false; });
}, true);

/* ---------- theme editor ---------- */
document.addEventListener('shopify:block:select', (e) => {
  const gal = e.target.closest('bb-gallery');
  if (gal && e.target.dataset.bbSlide !== undefined) gal.go(gal.slides.indexOf(e.target));
  const ann = e.target.closest('bb-announcement');
  if (ann && ann.msgs) { ann.stop(); ann.show(ann.msgs.indexOf(e.target)); }
  const car = e.target.closest('bb-carousel');
  if (car && car.track) car.reveal(e.target);
  const wall = e.target.closest('bb-review-wall');
  if (wall && e.target.hidden) e.target.hidden = false;
});
